import React, { useContext, useMemo, useState } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Space, Modal } from "antd";
import { ColumnProps } from "antd/lib/table";
import { BarsOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../table";
import styles from "./styles.module.scss";

export interface SortableTableItem {
  id: number | string;
  isActive?: boolean;
  [key: string]: any;
}

export interface SortableTableProps<T extends SortableTableItem> {
  dataSource: T[];
  columns: ColumnProps<T>[];
  loading?: boolean;
  showPagination?: boolean;
  total?: number;
  pageSize?: number;
  page?: number;
  onPaginationChange?: (page: number, pageSize: number) => void;
  onDragEnd?: (activeId: number | string, overId: number | string, oldIndex: number, newIndex: number) => boolean | void | Promise<boolean | void>;
  onToggleActive?: (id: number | string, newIsActive: boolean) => void | Promise<void>;
  showActiveToggle?: boolean;
  activeColumnKey?: string;
  rowKey?: string;
  className?: string;
  onChange?: (pagination: any, filters: any, sorter: any) => void;
  sortEnabled?: boolean;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<BarsOutlined />}
      className={styles.dragHandle}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const SortableRow: React.FC<{ "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

function SortableTable<T extends SortableTableItem>({
  dataSource,
  columns,
  loading = false,
  showPagination = true,
  total,
  pageSize,
  page,
  onPaginationChange,
  onDragEnd,
  onToggleActive,
  showActiveToggle = false,
  activeColumnKey = "isActive",
  rowKey = "id",
  className,
  onChange,
  sortEnabled = false,
}: SortableTableProps<T>) {
  const { t } = useTranslation();
  const [localDataSource, setLocalDataSource] = useState<T[]>(dataSource);
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; record: T | null; newIsActive: boolean }>({
    visible: false,
    record: null,
    newIsActive: false,
  });

  React.useEffect(() => {
    setLocalDataSource(dataSource);
  }, [dataSource]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeIndex = localDataSource.findIndex((item) => String(item[rowKey]) === String(activeId));
    const overIndex = localDataSource.findIndex((item) => String(item[rowKey]) === String(overId));

    if (activeIndex === -1 || overIndex === -1) return;

    if (onDragEnd) {
      const shouldMove = await onDragEnd(activeId, overId, activeIndex, overIndex);
      if (shouldMove === false) return;
    }

    const newDataSource = arrayMove(localDataSource, activeIndex, overIndex);
    setLocalDataSource(newDataSource);
  };

  const handleToggleActive = (record: T, newIsActive: boolean) => {
    if (onToggleActive) {
      setConfirmModal({
        visible: true,
        record,
        newIsActive,
      });
    }
  };

  const confirmToggleActive = async () => {
    if (confirmModal.record && onToggleActive) {
      await onToggleActive(confirmModal.record[rowKey], confirmModal.newIsActive);
      setConfirmModal({ visible: false, record: null, newIsActive: false });
    }
  };

  const cancelToggleActive = () => {
    setConfirmModal({ visible: false, record: null, newIsActive: false });
  };

  const enhancedColumns: ColumnProps<T>[] = useMemo(() => {
    const baseColumns = columns.filter((col) => col.key !== "sort" && col.key !== "actions" && (!showActiveToggle || col.key !== activeColumnKey));

    const sortColumn: ColumnProps<T> = {
      key: "sort",
      align: "center",
      width: 60,
      render: () => <DragHandle />,
    };

    const activeColumn: ColumnProps<T> = {
      title: t("is_active"),
      dataIndex: activeColumnKey,
      align: "center",
      key: activeColumnKey,
      width: 120,
      render: (value: boolean, record: T) => (
        <Space
          className={styles.activeToggle}
          onClick={() => handleToggleActive(record, !value)}
          style={{ cursor: "pointer" }}
        >
          {value ? (
            <>
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
              <span>{t("active")}</span>
            </>
          ) : (
            <>
              <CloseCircleOutlined style={{ color: "#d9d9d9" }} />
              <span>{t("inactive")}</span>
            </>
          )}
        </Space>
      ),
    };

    const result: ColumnProps<T>[] = sortEnabled ? [sortColumn, ...baseColumns] : [...baseColumns];

    if (showActiveToggle) {
      result.push(activeColumn);
    }

    const actionsColumn = columns.find((col) => col.key === "actions" || col.dataIndex === "");
    if (actionsColumn) {
      result.push(actionsColumn);
    }

    return result;
  }, [columns, showActiveToggle, activeColumnKey, t, sortEnabled]);

  const tableElement = (
    <Table<T>
      rowKey={rowKey}
      showPagination={showPagination}
      columns={enhancedColumns}
      dataSource={localDataSource}
      loading={loading}
      className={className}
      total={total}
      pageSize={pageSize}
      page={page}
      onPaginationChange={onPaginationChange}
      onChange={onChange}
      {...(sortEnabled
        ? {
            components: {
              body: {
                row: SortableRow,
              },
            },
          }
        : {})}
    />
  );

  return (
    <>
      {sortEnabled ? (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localDataSource.map((item) => item[rowKey])} strategy={verticalListSortingStrategy}>
            {tableElement}
          </SortableContext>
        </DndContext>
      ) : (
        tableElement
      )}

      <Modal
        title={confirmModal.newIsActive ? t("activate_item") : t("deactivate_item")}
        visible={confirmModal.visible}
        onOk={confirmToggleActive}
        onCancel={cancelToggleActive}
        okText={confirmModal.newIsActive ? t("activate") : t("deactivate")}
        cancelText={t("cancel")}
      >
        <p>
          {confirmModal.newIsActive
            ? t("are_you_sure_you_want_to_activate_this_item")
            : t("are_you_sure_you_want_to_deactivate_this_item")}
        </p>
      </Modal>
    </>
  );
}

export default SortableTable;
